import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Navigate, useLocation } from "react-router-dom";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "nex_token";

// Axios instance with token auto-attach
export const apiClient = axios.create({ baseURL: API });
apiClient.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const AuthCtx = createContext({ user: null, loading: true, login: async () => {}, logout: () => {} });

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const check = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await apiClient.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL (Emergent Google Auth playbook): if returning from OAuth callback,
    // skip the /auth/me check so AuthCallback can exchange the session_id first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    check();
  }, [check]);

  const login = async (email, password) => {
    const { data } = await apiClient.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const pinLogin = async (email, pin) => {
    const { data } = await apiClient.post("/auth/pin/login", { email, pin });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  // Exchange an Emergent Google session_id for a NEX JWT (allowlisted admins only).
  const googleExchange = async (sessionId) => {
    const { data } = await apiClient.post(
      "/auth/google/exchange",
      null,
      { headers: { "X-Session-ID": sessionId } }
    );
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  return <AuthCtx.Provider value={{ user, loading, login, pinLogin, googleExchange, logout }}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F19]">
        <div className="mono-body text-[11px] uppercase tracking-[0.3em] text-nex-gold animate-pulse-gold">Authenticating...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" state={{ from: location }} replace />;
  return children;
};

export const formatApiError = (err) => {
  const raw = err?.response?.data?.detail;
  if (Array.isArray(raw)) return raw.map((e) => e.msg || JSON.stringify(e)).join(" · ");
  return String(raw || err?.message || "Something went wrong");
};
